import React, { ReactNode, useMemo } from 'react';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { ColumnFilterButton } from 'src/core/ui/ColumnFilterButton';
import { TooltipPopover } from 'src/core/ui/TooltipPopover';
import { ArrowRight } from 'src/svgs/ArrowRight';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';

import { useFilterShiftSelect } from './useFilterShiftSelect';

/**
 * Container wrapper for filter popovers
 */
const Container = ({
  isActive,
  columnId,
  children,
  style,
}: {
  isActive: boolean;
  columnId?: string;
  children: ReactNode;
  style?: React.CSSProperties;
}) => (
  <TooltipPopover
    triggerContent={
      <ColumnFilterButton isActive={isActive} columnId={columnId} />
    }
    contentVariant="v2"
    showArrow={false}
    contentStyle={style}
    style={{ maxHeight: '400px', overflow: 'auto' }}
  >
    <Stack
      direction="column"
      gap="m"
      style={{
        padding: `${vars.spacing.xxl} ${vars.spacing.xl} ${vars.spacing.xl} ${vars.spacing.xl}`,
      }}
    >
      {children}
    </Stack>
  </TooltipPopover>
);

/**
 * Number range filter inputs (min/max)
 */
const RangeFilter = ({
  min,
  max,
  onChange,
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
  prefix,
  suffix,
}: {
  min?: number;
  max?: number;
  onChange: (value: { min?: number; max?: number }) => void;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
}) => {
  return (
    <Stack direction="row" justifyContent="spaceBetween" gap="m">
      <PosTextField
        type="number"
        value={min ?? ''}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          const newMinValue = e.target.value ? v : undefined;
          const newMaxValue =
            max != null ? (e.target.value ? Math.max(v, max) : max) : undefined;

          onChange({
            min: newMinValue,
            max: newMaxValue,
          });
        }}
        placeholder={minPlaceholder}
        onClick={(e) => e.stopPropagation()}
        prefixDisplay={prefix}
        postfixDisplay={suffix}
        rootProps={{
          style: {
            borderRadius: vars.borderRadius.base,
            fontSize: vars.typography.fontSize.base,
            padding: `${vars.spacing.sm} ${vars.spacing.m}`,
          },
        }}
      />
      <ArrowRight size={'40px'} />
      <PosTextField
        type="number"
        value={max ?? ''}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          const newMaxValue = e.target.value ? v : undefined;
          const newMinValue =
            min != null ? (e.target.value ? Math.min(v, min) : min) : undefined;

          onChange({
            min: newMinValue,
            max: newMaxValue,
          });
        }}
        placeholder={maxPlaceholder}
        onClick={(e) => e.stopPropagation()}
        prefixDisplay={prefix}
        postfixDisplay={suffix}
        rootProps={{
          style: {
            borderRadius: vars.borderRadius.base,
            fontSize: vars.typography.fontSize.base,
            padding: `${vars.spacing.sm} ${vars.spacing.m}`,
          },
        }}
      />
    </Stack>
  );
};

/**
 * Reusable search bar for filters
 */
const SearchBar = ({
  value,
  onChange,
  placeholder = 'Search...',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <PosTextField
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    onClick={(e) => e.stopPropagation()}
    prefixDisplay={
      <SearchSolidIcon
        size={vars.iconSize.s}
        fill={vars.color.iconPrimary}
        withHoverEffect
      />
    }
    style={{
      fontSize: vars.typography.fontSize.base,
      height: '20px',
      width: '100%',
    }}
    rootProps={{
      style: { borderRadius: vars.borderRadius.base, padding: '8px' },
    }}
  />
);

/**
 * Individual checkbox item for filter lists
 */
const CheckboxItem = ({
  itemKey,
  label,
  selected,
  count,
  onChange,
}: {
  itemKey: string;
  label: string;
  selected: boolean;
  count?: number;
  onChange: () => void;
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      cursor: 'pointer',
      fontSize: vars.typography.fontSize.base,
      width: '100%',
    }}
    data-testid={`filter-option-${itemKey}`}
    onClick={onChange}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <Checkbox onChange={() => {}} checked={selected} isV2Variant />
      <div
        style={
          selected
            ? {
                color: vars.color.textBrandV2,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }
            : {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }
        }
      >
        <Content id={label as any} />
      </div>
    </div>
    {count !== undefined && <span style={{ color: '#888' }}>[{count}]</span>}
  </div>
);

/**
 * Scrollable checkbox list for filters with shift+select support
 */
const List = ({
  options,
  value,
  onChange,
  counts,
}: {
  options: Record<string, string>;
  value: string[];
  onChange: (newValue: string[]) => void;
  counts?: Record<string, number>;
}) => {
  const { handleCheckboxClick } = useFilterShiftSelect();

  // Memoize sorted options to avoid re-sorting on every render
  // Sort by label in ascending order (A to Z for strings, numerically for numbers)
  const sortedOptions = useMemo(() => {
    // Check if all labels are numeric
    const allNumeric = Object.values(options).every(
      label => !isNaN(Number(label)) && label.trim() !== ''
    );

    return Object.entries(options).toSorted(([, labelA], [, labelB]) => {
      if (allNumeric) {
        // Numeric comparison
        return Number(labelA) - Number(labelB);
      } else {
        // String comparison with numeric awareness
        return labelA.localeCompare(labelB, undefined, { numeric: true });
      }
    });
  }, [options]);

  // Extract sorted keys array for shift-select to work correctly
  const sortedKeys = useMemo(
    () => sortedOptions.map(([key]) => key),
    [sortedOptions]
  );

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: vars.spacing.lg,
        maxHeight: '300px',
      }}
    >
      {sortedOptions.length === 0 ? (
        <div style={{ color: vars.color.textSecondaryV2 }}>
          <Content id={ContentId.NoResultFound} />
        </div>
      ) : (
        sortedOptions.map(([key, label], index) => (
          <CheckboxItem
            key={key}
            itemKey={key}
            label={label}
            selected={value.includes(key)}
            count={counts?.[key]}
            onChange={() =>
              handleCheckboxClick(index, key, value, sortedKeys, onChange)
            }
          />
        ))
      )}
    </div>
  );
};

/**
 * Clear button for filters
 */
const ClearButton = ({
  text,
  onClear,
  disabled,
  selectedCount,
}: {
  text?: string;
  onClear: () => void;
  disabled: boolean;
  selectedCount?: number;
}) => {
  return (
    <Button
      variant="textPlain"
      onClick={onClear}
      size="unset"
      disabled={disabled}
      style={{
        alignSelf: 'flex-start',
        fontSize: vars.typography.fontSize.sm,
        marginTop: vars.spacing.lg,
        color: vars.color.textSecondaryV2,
      }}
    >
      {selectedCount && selectedCount > 0 ? (
        <FormatContent
          id={FormatContentId.ClearSelections}
          params={[selectedCount.toString()]}
        />
      ) : (
        <Content id={text ? text : ContentId.ClearSelections} />
      )}
    </Button>
  );
};

// Export as namespace
export const ColumnFilter = {
  Container,
  RangeFilter,
  SearchBar,
  CheckboxItem,
  List,
  ClearButton,
};
